/**
 * Pastel Color Palette for Translation Highlights
 *
 * 15 distinct, readable pastel colors that work well with:
 * - Light backgrounds (PDF white pages)
 * - The warm cream Orbit background (#eeeae2)
 * - Text readability (dark text on light background)
 *
 * Each color has:
 * - bg: Background color for translation blob and paragraph highlight
 * - border: Border/accent color for visual connection
 */

export interface TranslationColor {
  bg: string;
  border: string;
  name: string;
}

export const TRANSLATION_COLORS: TranslationColor[] = [
  { bg: "#FFE4E1", border: "#FFB4A8", name: "Misty Rose" },
  { bg: "#E6F3FF", border: "#A8D4FF", name: "Alice Blue" },
  { bg: "#E8F5E9", border: "#A5D6A7", name: "Honeydew" },
  { bg: "#FFF8E1", border: "#FFE082", name: "Cosmic Latte" },
  { bg: "#F3E5F5", border: "#CE93D8", name: "Magnolia" },
  { bg: "#E0F7FA", border: "#80DEEA", name: "Light Cyan" },
  { bg: "#FBE9E7", border: "#FFAB91", name: "Seashell" },
  { bg: "#E8EAF6", border: "#9FA8DA", name: "Lavender" },
  { bg: "#F1F8E9", border: "#AED581", name: "Honeydew 2" },
  { bg: "#FFF3E0", border: "#FFCC80", name: "Papaya Whip" },
  { bg: "#FCE4EC", border: "#F48FB1", name: "Lavender Blush" },
  { bg: "#E1F5FE", border: "#81D4FA", name: "Light Blue" },
  { bg: "#F9FBE7", border: "#DCE775", name: "Light Goldenrod" },
  { bg: "#EFEBE9", border: "#BCAAA4", name: "Linen" },
  { bg: "#E0F2F1", border: "#80CBC4", name: "Azure" },
];

/**
 * Get color for a given index.
 * Cycles through the palette if index exceeds length.
 */
export function getColorForIndex(index: number): TranslationColor {
  return TRANSLATION_COLORS[index % TRANSLATION_COLORS.length];
}

/**
 * Get a semi-transparent version of the background color.
 * Used for unhovered paragraph highlights.
 */
export function getTransparentBg(index: number, opacity: number = 0.5): string {
  const color = getColorForIndex(index);
  // Convert hex to rgba
  const hex = color.bg.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}
